/* Scroll reveal, two levels, one move (assets/bento-blocks.css, "Motion"):

   - sections: every section of <main> that starts below the first screen
     reveals its content as it scrolls into view (`.lt-reveal` /
     `.is-revealed`), the pieces one after another. The content, not the
     section: the backgrounds stay put, because bento cards with open edges
     join neighbouring sections into one long surface, and fading whole
     sections showed the seams between them. The pieces are the children of
     what sits in the section's `.container-full` (the card's header and
     body, a row's cells); a section without that container fades whole;
   - grid cards: the cards of the product/search grid
     (snippets/collection-bento-page.liquid) and the blog grid
     (sections/main-blog-bento.liquid) that start below the first screen rise
     in as they scroll into view, the cards of one row one after another
     (`.lt-reveal-item`, the same rise as the cards of a product tab panel).
     Cards the collection script swaps in later (filters, sorting, pages —
     assets/collection-bento.js) are picked up by a MutationObserver.

   - large images: the image-with-text pictures and the image carousel's
     (`SETTLE`) start slightly enlarged (`.lt-settle`) and ease
     to their size once they are in view and loaded (`.is-settled`),
     whichever comes last; one already on screen and painted when this runs
     is left alone, it would jump.

   Loaded by layout/theme.liquid only when the "Reveal on scroll" theme
   setting is on, and never in the theme editor, where content waiting for
   the scroll would look missing. The lazy-image fade that goes with it is not
   here: it has to start before the first image, so it is an inline script in
   layout/theme.liquid (CSS in assets/lt-tokens.css).

   Only elements whose top is below the viewport are touched: what the
   shopper sees on arrival (the LCP image included) is never hidden, and
   without JavaScript nothing is. Visitors who ask for reduced motion get no
   reveal at all (checked here, and the CSS sits behind the same media query).

   Once an element has faded in, its classes come off, so no transform is
   left on it: a transformed ancestor would become the containing block of
   any `position: fixed` element inside. */
(function () {
  if (window.LeonartoReveal) return;
  window.LeonartoReveal = true;

  if (typeof IntersectionObserver !== 'function') return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;
  // A device or connection that would stutter through it (layout/theme.liquid).
  if (document.documentElement.classList.contains('lt-motion-lite')) return;

  var ITEMS = '.m-coll-grid__item, .m-blog-bento__item';
  var STAGGER = 60; /* ms between the cards of one batch */
  var STAGGER_MAX = 5; /* cards past the sixth of a batch start with the sixth */
  var PIECE_STAGGER = 90; /* ms between the pieces of one section */
  var PIECE_STAGGER_MAX = 4;
  var SETTLE = '.m-bento-image__layer, .m-image-carousel-bento__img';
  var SETTLE_FALLBACK = 2500; /* ms: past --lt-dur-settle, in case transitionend never comes */

  function each(list, callback) {
    Array.prototype.forEach.call(list, callback);
  }

  function belowFold(element) {
    var rect = element.getBoundingClientRect();
    return rect.height > 0 && rect.top >= window.innerHeight;
  }

  function isPiece(element) {
    if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(element.tagName)) return false;
    var style = window.getComputedStyle(element);
    return style.display !== 'none' && style.position !== 'absolute' && style.position !== 'fixed';
  }

  /* What a section reveals: the children of each element in its
     `.container-full` (the card's content, a row's cells); an element with
     no children of its own goes whole. No container: the section itself. */
  function pieces(section) {
    var container = section.querySelector('.container-full');
    if (!container) return [section];
    var list = [];
    each(container.children, function (wrapper) {
      if (!isPiece(wrapper)) return;
      var children = Array.prototype.filter.call(wrapper.children, isPiece);
      if (children.length) list.push.apply(list, children);
      else list.push(wrapper);
    });
    return list.length ? list : [section];
  }

  /* Takes the classes off after the fade. transitionend never fires when the
     transition is cut short (tab in the background, element removed), so a
     timer settles it anyway. */
  function reveal(element, classes, delay) {
    if (delay) element.style.setProperty('--lt-reveal-delay', delay + 'ms');
    element.classList.add('is-revealed');
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      element.classList.remove(classes, 'is-revealed');
      element.style.removeProperty('--lt-reveal-delay');
    };
    element.addEventListener('transitionend', function onEnd(event) {
      if (event.target !== element || event.propertyName !== 'opacity') return;
      element.removeEventListener('transitionend', onEnd);
      finish();
    });
    window.setTimeout(finish, 1500 + (delay || 0));
  }

  var sectionPieces = new WeakMap();
  var sectionObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        sectionObserver.unobserve(entry.target);
        (sectionPieces.get(entry.target) || []).forEach(function (piece, index) {
          reveal(piece, 'lt-reveal', Math.min(index, PIECE_STAGGER_MAX) * PIECE_STAGGER);
        });
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  /* The cards that come into view together (one row, or several on a fast
     scroll) arrive in one callback, in document order: that order is the
     stagger. */
  var itemObserver = new IntersectionObserver(
    function (entries) {
      var index = 0;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        itemObserver.unobserve(entry.target);
        reveal(entry.target, 'lt-reveal-item', Math.min(index, STAGGER_MAX) * STAGGER);
        index++;
      });
    },
    { rootMargin: '0px 0px -5% 0px' }
  );

  function imageOf(element) {
    return element.tagName === 'IMG' ? element : element.querySelector('img');
  }

  function onScreen(element) {
    var rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function whenLoaded(img, callback) {
    if (img.complete) {
      callback();
      return;
    }
    var done = function () {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      callback();
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
  }

  /* Like reveal(): the classes come off once the image has settled. */
  function settle(element) {
    window.requestAnimationFrame(function () {
      element.classList.add('is-settled');
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        element.classList.remove('lt-settle', 'is-settled');
      };
      element.addEventListener('transitionend', function onEnd(event) {
        if (event.target !== element || event.propertyName !== 'scale') return;
        element.removeEventListener('transitionend', onEnd);
        finish();
      });
      window.setTimeout(finish, SETTLE_FALLBACK);
    });
  }

  var settleObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        settleObserver.unobserve(entry.target);
        whenLoaded(imageOf(entry.target), function () {
          settle(entry.target);
        });
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  function watchSettle(root) {
    each(root.querySelectorAll(SETTLE), function (element) {
      var img = imageOf(element);
      if (!img || (img.complete && onScreen(element))) return;
      element.classList.add('lt-settle');
      settleObserver.observe(element);
    });
  }

  function watchItems(root) {
    var items = root.matches && root.matches(ITEMS) ? [root] : root.querySelectorAll ? root.querySelectorAll(ITEMS) : [];
    each(items, function (item) {
      if (item.classList.contains('lt-reveal-item') || item.closest('.lt-reveal')) return;
      if (!belowFold(item)) return;
      item.classList.add('lt-reveal-item');
      itemObserver.observe(item);
    });
  }

  function init() {
    var main = document.getElementById('MainContent');
    if (!main) return;

    each(main.children, function (section) {
      if (!section.classList.contains('shopify-section') || !belowFold(section)) return;
      var list = pieces(section);
      list.forEach(function (piece) {
        piece.classList.add('lt-reveal');
      });
      sectionPieces.set(section, list);
      sectionObserver.observe(section);
    });

    watchItems(main);
    watchSettle(main);

    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        each(record.addedNodes, function (node) {
          if (node.nodeType === 1) watchItems(node);
        });
      });
    }).observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
